import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface FetchPartsFilters {
  featured?: boolean;
  status?: string;
  listingTypes?: string[];
  limit?: number;
  enabled?: boolean;
}

export const useFeaturedPartsListings = (filters: FetchPartsFilters = {}) => {
  const { enabled = true, ...queryFilters } = filters;
  
  return useQuery({
    queryKey: ['featured-parts', queryFilters],
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('parts_listings')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.listingTypes && filters.listingTypes.length > 0) {
        query = query.in('listing_type', filters.listingTypes);
      }

      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      if (!data || data.length === 0) return [];
      
      // BATCH: Fetch all media in a single query instead of N queries
      const listingIds = data.map(l => l.id);
      
      const { data: allMedia } = await supabase
        .from('listing_media')
        .select('listing_id, file_url, metadata, display_order')
        .in('listing_id', listingIds)
        .eq('media_type', 'photo')
        .order('display_order', { ascending: true });

      // Process media for each listing client-side
      const listingsWithMedia = data.map((listing) => {
        const media = (allMedia || []).filter(m => m.listing_id === listing.id);
        const urls = media.map(m => m.file_url).filter(Boolean);
        const heroImage = listing.hero_image || urls[0] || null;
        
        return {
          ...listing,
          hero_image: heroImage,
          photos: urls,
        };
      });
      
      return listingsWithMedia;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
